import React, { useState } from "react";
import styles from "./style.module.scss";
import { Logo, LanguageInfo } from "@components";
import { FooterSectionType, FOOTER_SECTIONS, FOOTER_SECTIONS_ES, FOOTER_SECTIONS_PT } from "./constants";
import { useNavigate, useParams } from "react-router-dom";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { ReactComponent as FacebookIcon } from "./assets/facebook.svg";
import { ReactComponent as InstagramIcon } from "./assets/instagram.svg";
import { ReactComponent as TwitterIcon } from "./assets/twitter.svg";

const FooterSection: React.FC<FooterSectionType & { currentLang?: string }> = ({ links, title, currentLang }) => {
	const navigate = useNavigate();
	
	const getLocalizedLink = (link: string) => {
		// Если это внешняя ссылка, возвращаем как есть
		if (link.startsWith('http')) return link;
		
		// Pages that should NOT have language routes (English only)
		const englishOnlyPages = [
			'/bypass-ai-detection',
			'/gptzero-alternative', 
			'/shadowgpt',
			'/pricing',
			'/auth'
		];
		
		// If this is an English-only page, don't add language prefix
		if (englishOnlyPages.includes(link)) {
			return link;
		}
		
		// Если есть текущий язык и это не дефолтный путь, добавляем язык только для поддерживаемых страниц
		if (currentLang && link.startsWith('/')) {
			// Only add language prefix for main page and free tools
			if (link === '/' || link === '/summarizer' || link === '/paragraph-generator' || link === '/text-message-generator' || link === '/abstract-generator') {
				return `/${currentLang}${link}`;
			}
		}
		
		return link;
	};
	
	const handleClick = (link: string, isExternal: boolean) => {
		if (isExternal) return; // Пусть браузер обрабатывает внешние ссылки
		
		const localizedLink = getLocalizedLink(link);
		navigate(localizedLink);
	};

	return (
		<div className={styles.FooterSection}>
			<h4>{title}</h4>
			<div className={styles.FooterSection__linksContainer}>
				{links.map((el: any, index: number) => {
					const isExternal = el.link.startsWith('http');
					const finalLink = getLocalizedLink(el.link);
					
					return (
						<a
							key={index}
							href={finalLink}
							onClick={!isExternal ? (e) => {
								e.preventDefault();
								handleClick(el.link, isExternal);
							} : undefined}
							target={isExternal ? "_blank" : undefined}
							className={styles.FooterSection__linkContainer}
							rel={isExternal ? "noreferrer" : undefined}
						>
							{el.text === "Instagram" ? <InstagramIcon /> : null}
							{el.text === "Twitter" ? <TwitterIcon /> : null}
							{el.text === "Facebook" ? <FacebookIcon /> : null}
							<p className={styles.FooterSection__link}>{el.text}</p>
						</a>
					);
				})}
			</div>
		</div>
	);
};

export const Footer: React.FC = () => {
	const { lang } = useParams<{ lang: string }>();
	const { i18n } = useTranslation();
	const currentLang = lang || i18n.language || '';
	
	// Use language-specific footer sections when Spanish or Portuguese is selected
	const footerSections = currentLang === 'es' ? FOOTER_SECTIONS_ES : 
	                      currentLang === 'pt' ? FOOTER_SECTIONS_PT : 
	                      FOOTER_SECTIONS;
	
	const renderSections = (data: FooterSectionType[]) =>
		data.map((it, index) => <FooterSection key={index} {...it} currentLang={currentLang} />);

	const navigate = useNavigate();
	const [isShowLang, setIsShowLang] = useState<boolean>(false);

	return (
		<div className={`app-container ${styles.Footer}`}>
			<div className={styles.Footer__flexContainer}>
				<Logo />
				<div className={styles.Footer__sectionContainer}>
					{renderSections(footerSections)}
				</div>
			</div>
			<div className={styles.Copyright}>
				<small>{i18next.t("footer.copyright")}</small>
				<a href="https://shadowgpt.userjot.com/" target="_blank" rel="noopener noreferrer">
					Suggest a Feature
				</a>
				<div className={styles.Copyright__flexWrapper}>
					<LanguageInfo set={setIsShowLang} val={isShowLang} position={{ bottom: "30px" }} />
					<a 
						href={currentLang ? `/${currentLang}/terms/privacy` : "/terms/privacy"}
						onClick={(e) => {
							e.preventDefault();
							navigate(currentLang ? `/${currentLang}/terms/privacy` : "/terms/privacy");
						}}
					>
						{i18next.t("footer.privacy")}
					</a>
					<a 
						href={currentLang ? `/${currentLang}/terms/conditions` : "/terms/conditions"}
						onClick={(e) => {
							e.preventDefault();
							navigate(currentLang ? `/${currentLang}/terms/conditions` : "/terms/conditions");
						}}
					>
						{i18next.t("footer.terms")}
					</a>
				</div>
			</div>
		</div>
	);
};
